import * as React from 'react'
import {
  ConfigureMapping,
  CreateMigration,
  MigrationPlan,
  DataResetInProgress,
  LicenseMigrationInProgress,
  LicenseMigrationInstructions,
} from './migration'
import { useCustomerApi, usePageNotifications, useLogger } from 'hooks'
import {
  CustomerDataMigrationViewModel,
  MigrationStage,
  MigrationStageStatus,
} from '../../api'
import { Loader } from 'cs-kevlar-react'
import { CustomerDetailPaneBaseProps } from './customerDetailPane'

interface DataMigrationProps extends CustomerDetailPaneBaseProps {}

export const DataMigration: React.FunctionComponent<DataMigrationProps> = ({
  customerAgencyId,
  onClose,
}) => {
  const {
    getCustomerMigrationStatus,
    createCustomerMigration,
    uploadMigrationMappingFile,
    resetMigrationData,
    migrateLicenses,
    cancelCustomerMigration,
  } = useCustomerApi()
  const { logError } = useLogger()
  const { dispatch: dispatchNotification } = usePageNotifications()
  const [
    customerMigrationStatus,
    setCustomerMigrationStatus,
  ] = React.useState<CustomerDataMigrationViewModel | null>(null)
  const [requiresRefresh, setRequiresRefresh] = React.useState<number>(
    Date.now(),
  )
  const [mappingFile, setMappingFile] = React.useState<File | null>(null)

  const getCurrentMigrationStatus = () => {
    getCustomerMigrationStatus(customerAgencyId)
      .then((response) => {
        if (!response) return

        if (response.status === 200 && response.data) {
          setCustomerMigrationStatus(response.data)
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error loading the migration information.',
          })
        }
      })
      .catch((error) => {
        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error loading the migration information.',
        })
      })
  }

  React.useEffect(() => {
    getCurrentMigrationStatus()
  }, [requiresRefresh])

  const refresh = () => {
    setRequiresRefresh(Date.now())
  }

  const createMigration = (values: any, action: any) => {
    createCustomerMigration(customerAgencyId)
      .then((response) => {
        if (!response) return

        action?.setSubmitting(false)

        if (response.status === 200 && response.data.isSuccess) {
          refresh()
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error creating the data migration.',
          })
        }
      })
      .catch((error) => {
        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error creating the data migration.',
        })

        action?.setSubmitting(false)
      })
  }

  const uploadMappingFile = () => {
    if (mappingFile === null) return

    uploadMigrationMappingFile(customerAgencyId, mappingFile)
      .then((response) => {
        if (!response) return

        if (response.status === 200 && response.data.isSuccess) {
          // null out the current mapping file and trigger a new refresh
          refresh()
          setMappingFile(null)
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error uploading the data mapping file.',
          })
        }
      })
      .catch((error) => {
        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error uploading the data mapping file.',
        })
      })
  }

  const onLicenseMigrationClicked = (values: any, action: any) => {
    migrateLicenses(customerAgencyId)
      .then((response) => {
        if (!response) return

        action?.setSubmitting(false)
        if (response.status === 200 && response.data.isSuccess) {
          refresh()
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: `There was an error migrating the licenses.  ${response.data.errorMessage}`,
          })
        }
      })
      .catch((error) => {
        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error migrating the licenses.',
        })

        action?.setSubmitting(false)
      })
  }

  const onResetDataClicked = (values: any, action: any) => {
    resetMigrationData(customerAgencyId)
      .then((response) => {
        if (!response) return

        action?.setSubmitting(false)

        if (response.status === 200 && response.data.isSuccess) {
          refresh()
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: `There was an error resetting the data.  ${response.data.errorMessage}`,
          })
        }
      })
      .catch((error) => {
        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error resetting the data.',
        })

        action?.setSubmitting(false)
      })
  }

  const cancelMigration = () => {
    cancelCustomerMigration(customerAgencyId).then(
      (response) => {
        if (response === null) {
          onClose()
          return
        }

        if (response.data.isSuccess) {
          onClose()
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error canceling the migration.',
          })
        }
      },
      (error) => {
        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error canceling the migration.',
        })
      },
    )
  }

  if (customerMigrationStatus === null) {
    return <Loader size="sm" text="Loading Results" />
  }

  // Create the migration
  if (
    customerMigrationStatus.migrationContext === null ||
    (customerMigrationStatus.migrationContext.stage ===
      MigrationStage.LicenseMigration &&
      customerMigrationStatus.migrationContext.stageStatus ===
        MigrationStageStatus.Complete)
  ) {
    return (
      <CreateMigration
        createMigration={createMigration}
        migrationContext={customerMigrationStatus.migrationContext}
        migrationResult={customerMigrationStatus.migrationResult}
      >
        <MigrationPlan
          context={customerMigrationStatus.migrationContext}
          result={customerMigrationStatus.migrationResult}
        />
      </CreateMigration>
    )
  }

  if (
    customerMigrationStatus.migrationContext.stage ===
      MigrationStage.DataReset &&
    customerMigrationStatus.migrationContext.stageStatus !==
      MigrationStageStatus.Complete
  ) {
    return (
      <ConfigureMapping
        onFileSelected={setMappingFile}
        uploadMappingFile={uploadMappingFile}
        migrationContext={customerMigrationStatus.migrationContext}
        migrationResult={customerMigrationStatus.migrationResult}
        onResetDataClicked={onResetDataClicked}
        onCancel={cancelMigration}
      >
        <MigrationPlan
          context={customerMigrationStatus.migrationContext}
          result={customerMigrationStatus.migrationResult}
        />
      </ConfigureMapping>
    )
  }

  if (
    customerMigrationStatus.migrationContext.stage ===
      MigrationStage.DataReset &&
    customerMigrationStatus.migrationContext.stageStatus !==
      MigrationStageStatus.Running &&
    customerMigrationStatus.migrationContext.stageStatus !==
      MigrationStageStatus.Complete
  ) {
    return (
      <ConfigureMapping
        onFileSelected={setMappingFile}
        uploadMappingFile={uploadMappingFile}
        migrationContext={customerMigrationStatus.migrationContext}
        migrationResult={customerMigrationStatus.migrationResult}
        onResetDataClicked={onResetDataClicked}
        onCancel={cancelMigration}
      >
        <MigrationPlan
          context={customerMigrationStatus.migrationContext}
          result={customerMigrationStatus.migrationResult}
        />
      </ConfigureMapping>
    )
  }

  // Data reset in progress
  if (
    customerMigrationStatus.migrationContext.stage ===
      MigrationStage.DataReset &&
    customerMigrationStatus.migrationContext.stageStatus ===
      MigrationStageStatus.Running
  ) {
    return (
      <DataResetInProgress refresh={refresh}>
        <MigrationPlan
          context={customerMigrationStatus.migrationContext}
          result={customerMigrationStatus.migrationResult}
        />
      </DataResetInProgress>
    )
  }

  if (
    (customerMigrationStatus.migrationContext.stage ===
      MigrationStage.DataReset &&
      customerMigrationStatus.migrationContext.stageStatus ===
        MigrationStageStatus.Complete) ||
    (customerMigrationStatus.migrationContext.stage ===
      MigrationStage.LicenseMigration &&
      customerMigrationStatus.migrationContext.stageStatus ===
        MigrationStageStatus.None)
  ) {
    return (
      <LicenseMigrationInstructions
        refresh={refresh}
        customerMigrationStatus={customerMigrationStatus}
        onLicenseMigrationConfirmed={onLicenseMigrationClicked}
      >
        <MigrationPlan
          context={customerMigrationStatus.migrationContext}
          result={customerMigrationStatus.migrationResult}
        />
      </LicenseMigrationInstructions>
    )
  }

  // License migration in progress
  if (
    customerMigrationStatus.migrationContext.stage ===
      MigrationStage.LicenseMigration &&
    customerMigrationStatus.migrationContext.stageStatus ===
      MigrationStageStatus.Running
  ) {
    return (
      <LicenseMigrationInProgress refresh={refresh}>
        <MigrationPlan
          context={customerMigrationStatus.migrationContext}
          result={customerMigrationStatus.migrationResult}
        />
      </LicenseMigrationInProgress>
    )
  }

  logError('Invalid migration status')
  return null
}
